import { takeEvery, put, select, all } from 'redux-saga/effects';
import { toastr } from 'react-redux-toastr';

import { loaderDecorator } from '../decorators/loader-decorator';

import * as types from '../../actions/candidate/candidate-application/types';
import * as actions from '../../actions/candidate/candidate-application';
import { getApplicationList } from '../../actions/candidate/candidate-applications';

import httpStatusCodes from '../../constants/http-status-codes';

import apiFetcher from '../../tools/api-fetcher';
import history from '../../tools/history';
import handleError from '../../tools/error-helper';
import { openPrintVersion } from '../../tools/print-helper';

function* fetchApplicationSummaryById({ applicationId }) {
  yield put(actions.setApplicationSummaryLoading(true));

  try {
    const { data } = yield apiFetcher
      .auth()
      .get(`/candidates/current/applications/${applicationId}/summary`);

    yield put(actions.setApplicationSummary(data));
  } catch (exception) {
    switch (exception.response && exception.response.status) {
      case httpStatusCodes.NotFound:
        yield put(
          actions.setApplicationSummaryRequestFailedCode(
            exception.response.status
          )
        );
        break;
      default:
        handleError(exception);
        break;
    }
  }

  yield put(actions.setApplicationSummaryLoading(false));
}

function* getCandidateApplicationCoverLetter({ applicationId }) {
  yield put(actions.setCandidateApplicationCoverLetterLoading(true));

  try {
    const { data } = yield apiFetcher
      .auth()
      .get(`/candidates/current/applications/${applicationId}/cover-letter`);

    yield put(actions.setCandidateApplicationCoverLetter(data));
  } catch (exception) {
    yield handleError(exception);
  }

  yield put(actions.setCandidateApplicationCoverLetterLoading(false));
}

function* getCandidateApplicationSupplementaryQuestions({ applicationId }) {
  yield put(actions.setCandidateApplicationSupplementaryQuestionsLoading(true));

  try {
    const { data } = yield apiFetcher
      .auth()
      .get(
        `/candidates/current/applications/${applicationId}/supplementary-questions`
      );

    yield put(actions.setCandidateApplicationSupplementaryQuestions(data));
  } catch (exception) {
    yield handleError(exception);
  }

  yield put(
    actions.setCandidateApplicationSupplementaryQuestionsLoading(false)
  );
}

function* reloadApplicationContextData(applicationId) {
  const appList = yield select(state => state.candidateApplicationList);
  const application = yield select(state => state.candidateApplication);

  if (application.get('applicationSummary')) {
    yield put(actions.fetchApplicationSummaryById(applicationId));
  }

  if (appList.get('list').get('results').size) {
    yield put(getApplicationList(appList.get('queryParams').toJS()));
  }
}

function* withdrawApplication({ applicationId, reasonId, query }) {
  const body = {
    applicationId: applicationId,
    reasonId: reasonId
  };
  yield put(actions.setCandidateApplicationWithdrawing(true));
  try {
    yield apiFetcher
      .auth()
      .post(`/candidates/current/applications/withdrawn`, body);
    yield reloadApplicationContextData(applicationId);
    yield toastr.success('Application was successfully withdrawn.');
  } catch (exception) {
    yield handleError(exception);
  }
  yield put(actions.setCandidateApplicationWithdrawing(false));
}

function* submitApplication(applicationId) {
  const body = {
    applicationId: applicationId
  };
  yield apiFetcher.auth().post('/candidates/current/applications/submit', body);
}

function* reSubmitApplication({ applicationId }) {
  const messagesForHttpStatusCodes = {
    [httpStatusCodes.NotFound]: 'Application not found.',
    [httpStatusCodes.Gone]: "Vacancy doesn't exist anymore.",
    [httpStatusCodes.Locked]: 'Vacancy has expired apply by date.',
    [httpStatusCodes.InternalServerError]: 'Internal server error.'
  };

  yield put(actions.setCandidateApplicationReSubmitting(true));

  try {
    yield submitApplication(applicationId);
    yield reloadApplicationContextData(applicationId);
    yield toastr.success('Application was successfully re-submitted.');

    yield apiFetcher
      .auth()
      .get(
        `/candidates/current/applications/${applicationId}/supplementary-questions`
      )
      .then(response => {
        if (
          response &&
          response.data &&
          response.data.supplementaryQuestionSummaries &&
          response.data.supplementaryQuestionSummaries.filter(
            q => q.submitted === false
          ).length !== 0
        ) {
          toastr.warning(
            'There are new Supplementary Questions for this application.'
          );
          history.push(
            `/candidate/my-applications/${applicationId}/details/supplementary`
          );
        }
      });
  } catch (exception) {
    if (exception.response) {
      const { status } = exception.response;
      yield handleError(
        exception,
        `Failed to re-submit the application. ${messagesForHttpStatusCodes[
          status
        ] || null}`
      );
    }
  }
  yield put(actions.setCandidateApplicationReSubmitting(false));
}

function* printCandidateApplication({ applicationId }) {
  try {
    yield openPrintVersion(
      `candidates/current/applications/${applicationId}/print-version`
    );
  } catch (exception) {
    yield handleError(
      exception,
      'Failed to load candidate application print version.'
    );
  }
}

export default function* applicationSaga() {
  yield all([
    takeEvery(
      types.CANDIDATE_APPLICATION_FETCH_APPLICATION_SUMMARY_BY_ID,
      fetchApplicationSummaryById
    ),
    takeEvery(
      types.CANDIDATE_APPLICATION_GET_COVER_LETTER,
      getCandidateApplicationCoverLetter
    ),
    takeEvery(
      types.CANDIDATE_APPLICATION_GET_SUPPLEMENTARY_QUESTIONS,
      getCandidateApplicationSupplementaryQuestions
    ),
    takeEvery(types.CANDIDATE_APPLICATION_WITHDRAW, withdrawApplication),
    takeEvery(types.CANDIDATE_APPLICATION_RESUBMIT, reSubmitApplication),
    takeEvery(
      types.CANDIDATE_APPLICATION_PRINT_CANDIDATE_APPLICATION,
      loaderDecorator(
        printCandidateApplication,
        actions.setCandidateApplicationPrintVersionIsLoading
      )
    )
  ]);
}



// WEBPACK FOOTER //
// ./src/sagas/candidate/candidate-application.js